/**
 * Auth Initializer - Handles authentication restoration on app startup
 * UPDATED: Loads joined events from database instead of localStorage
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { getAuthUserId } from '../utils/authUser';
import { restoreAuth, loadJoinedEventsAsync, logout, updateUser } from '../store/slices/authSlice';
import { buildEventsApiUrl } from '../config/api';
import { buildAuthHeaders } from '../utils/authFetch';

const AuthInitializer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, initialized, user } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user);
  const [sessionValidated, setSessionValidated] = useState(false);

  useEffect(() => {
    console.log('🔧 AuthInitializer: Starting authentication restoration...');
    
    // Restore authentication from localStorage
    dispatch(restoreAuth());
  }, [dispatch]);

  useEffect(() => {
    if (!initialized) return;
    if (!isAuthenticated || !userId) {
      setSessionValidated(false);
      return;
    }

    let cancelled = false;
    const validateSession = async () => {
      try {
        const response = await fetch(buildEventsApiUrl('/api/auth/session'), { headers: buildAuthHeaders(userId) });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.user) {
          if (!cancelled) dispatch(logout());
          return;
        }
        if (!cancelled) {
          dispatch(updateUser(data.user));
          setSessionValidated(true);
        }
      } catch {
        if (!cancelled) dispatch(logout());
      }
    };

    setSessionValidated(false);
    validateSession();
    return () => { cancelled = true; };
  }, [dispatch, initialized, isAuthenticated, userId]);

  useEffect(() => {
    // After authentication is restored, load joined events from database
    if (isAuthenticated && sessionValidated && userId) {
      console.log(`📋 AuthInitializer: Loading joined events for authenticated user ${user?.firstName || 'user'} (${userId})`);
      dispatch(loadJoinedEventsAsync(userId));
    }
  }, [dispatch, isAuthenticated, sessionValidated, user?.firstName, userId]);

  // This component doesn't render anything
  return null;
};

export default AuthInitializer;
